/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import getColor from "@insite/mobius/utilities/getColor";
import { TabProps } from "@stoneage/MobiusOverrides/Tab/Tab";
import { TabGroupProps } from "@stoneage/MobiusOverrides/TabGroup/TabGroup";
import { xBaseTheme } from "@stoneage/XDL/types";
import { css } from "styled-components";

export default {
    defaultProps: {
        mergeCss: true,
        css: css`
            outline: none;
            flex: 1 1 auto;
            text-align: center;
            height: 44px;
            min-width: auto;
            ovewrflow-y: hidden;
            white-space: nowrap;
            position: relative;
            font-size: 20px;
            font-weight: bold;
            letter-spacing: 0;
            line-height: 24px;
            padding: 4px 12px 12px;
            transition: color 0.2s;
            cursor: pointer;
            width: ${({ currentTabIndex }: TabProps) => {
                if (currentTabIndex || currentTabIndex === 0) {
                    return "auto";
                }
                return "auto";
            }};
            flex: unset;
            border-bottom-width: 0;
            background: ${getColor("transparent")};
            color: ${({ selected, color }: TabProps) =>
                color ? getColor(color) : selected ? getColor("text.main") : getColor("secondary")};

            &:first-child {
                text-align: start;
                padding-left: 0;
            }
            &:after {
                content: "";
                position: absolute;
                bottom: 3px;
                left: 0;
                height: 6px;
                width: 100%;
                background-color: ${({ selected }: TabProps) => (selected ? getColor("primary") : "transparent")};
            }
            &:hover,
            &:focus {
                color: ${getColor("primary")};
                border-bottom-color: transparent;
                border-bottom-width: 0;
                &:after {
                    content: "";
                    position: absolute;
                    bottom: 3px;
                    left: 0;
                    height: 6px;
                    width: 100%;
                    background-color: ${getColor("primary")};
                }
            }

            &:focus,
            &:active {
                outline: none !important;
                color: ${({ color }: TabProps) => (color ? getColor(color) : getColor("text.main"))};

                &:after {
                    content: "";
                    position: absolute;
                    bottom: 3px;
                    left: 0;
                    height: 6px;
                    width: 100%;
                    background-color: ${getColor("primary")};
                }
            }
        `,
    },
    groupDefaultProps: {
        mergeCss: true,
        cssOverrides: {
            wrapper: css`
                padding: 0;
                display: block;
            `,
            tabGroup: css`
                overflow-y: auto;
                position: relative;
                padding-bottom: 0;
                padding: 0;
                z-index: 0;
                min-height: 35px;
                justify-content: ${({ placement }: TabGroupProps) =>
                    placement === "right" ? "flex-end" : "flex-start"};
                &::after {
                    border-top: 1px solid ${getColor("common.ui6")};
                    content: "";
                    height: 1px;
                    position: absolute;
                    bottom: 5px;
                    z-index: -1;
                    overflow: hidden;
                    width: 100%;
                }
                &.dynamic-animation > div {
                    position: relative;
                    &::before {
                        background-color: ${getColor("primary")};
                        content: "";
                        height: 6px;
                        position: absolute;
                        left: 0;
                        bottom: 3px;
                        z-index: 1;
                        overflow: hidden;
                        width: ${({ tabWidth }: TabGroupProps) => `${tabWidth as any}px`};
                        transition: 0.6s;
                        transform: translateX(${({ tabMovement }: TabGroupProps) => `${(tabMovement as any) ?? 0}px`});
                    }
                }
            `,
            tabContent: css`
                border: none;
                margin: 25px 0;
            `,
        },
    },
} as xBaseTheme["tab"];
